import { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { getToken, logout } from "../../api/auth";
import { agentsKpi } from "../../api/performance";
import {
  getMostCompletedOrders,
  getDriverKpi,
  getControllerKpi,
  getTotalCompletedOrders,
} from "../../api/dashboard";
import { getAllUsers } from "../../api/user";
import {
  FaTrophy,
  FaMedal,
  FaBox,
  FaSync,
  FaStar,
  FaCheckCircle,
  FaSignOutAlt,
  FaUser,
} from "react-icons/fa";
import { FaTruck } from "react-icons/fa";
import { GrUserWorker } from "react-icons/gr";
import { FaUserEdit } from "react-icons/fa";
import "./Dashboard.css";

const Dashboard = () => {
  const token = getToken();
  const [kpiData, setKpiData] = useState([]);
  const [controllersKpiData, setControllersKpiData] = useState([]);
  const [driversKpiData, setDriversKpiData] = useState([]);
  const [allKpiData, setAllKpiData] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [activeUserIds, setActiveUserIds] = useState([]);
  const [topAgents, setTopAgents] = useState({
    daily: [],
    monthly: [],
    yearly: [],
  });
  const [totalOrders, setTotalOrders] = useState(0);
  const [error, setError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    window.dispatchEvent(new Event("authStateChanged"));
    navigate("/", { replace: true });
  };

  const fetchData = useCallback(async () => {
    try {
      setError("");

      // Fetch all users and filter active ones
      const usersResponse = await getAllUsers();
      console.log("Users response:", usersResponse);
      const users = usersResponse?.users || usersResponse || [];
      console.log("Users array:", users);
      const activeUsernames = users
        .filter((user) => user.isActive)
        .map(
          (user) => user.username || user.userName || user.userId || user.id,
        );
      console.log("Active usernames:", activeUsernames);
      setActiveUserIds(activeUsernames);

      const kpiResponse = await agentsKpi();
      console.log("KPI Response:", kpiResponse);
      const agentsWithType = (kpiResponse.kpiData || [])
        .filter((agent) => {
          console.log(
            "Agent userId:",
            agent.userId,
            "In active:",
            activeUsernames.includes(agent.userId),
          );
          return activeUsernames.includes(agent.userId);
        })
        .map((agent) => ({
          ...agent,
          userType: "agent",
        }));
      console.log("Filtered agents:", agentsWithType);
      setKpiData(agentsWithType);

      const controllersResponse = await getControllerKpi();
      const controllersWithType = (controllersResponse.kpiData || [])
        .filter((controller) => activeUsernames.includes(controller.userId))
        .map((controller) => ({
          ...controller,
          userType: "controller",
        }));
      setControllersKpiData(controllersWithType);

      const driversResponse = await getDriverKpi();
      // Normalize driver data to match agent/controller structure
      const normalizedDrivers = (driversResponse.kpiData || [])
        .filter((driver) => activeUsernames.includes(driver.userId))
        .map((driver) => ({
          ...driver,
          correctOrders: driver.deliveredOrders || 0,
          incorrectOrders: 0,
          incorrectLines: 0,
          incorrectWeight: 0,
          incorrectVolume: 0,
          incorrectQuantity: 0,
          userType: "driver",
        }));
      setDriversKpiData(normalizedDrivers);

      const topAgentsResponse = await getMostCompletedOrders();
      if (topAgentsResponse.success) {
        const daily = topAgentsResponse.mostCompletedOrders
          .filter((agent) => agent.periodType === "Daily")
          .slice(0, 3);
        const monthly = topAgentsResponse.mostCompletedOrders
          .filter((agent) => agent.periodType === "Monthly")
          .slice(0, 3);
        const yearly = topAgentsResponse.mostCompletedOrders
          .filter((agent) => agent.periodType === "Yearly")
          .slice(0, 3);
        setTopAgents({ daily, monthly, yearly });
      }

      const totalOrdersResponse = await getTotalCompletedOrders();
      if (totalOrdersResponse.success) {
        setTotalOrders(totalOrdersResponse.totalCompletedOrders.monthly || 0);
      }
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
      console.error("Error fetching data:", err);
    }
  }, []);

  const calculatePerformanceScore = (agent) => {
    if (!agent) return 0;
    const totalCorrect =
      (agent.correctOrders || 0) +
      (agent.correctLines || 0) +
      (agent.correctWeight || 0) +
      (agent.correctVolume || 0) +
      (agent.correctQuantity || 0);
    const totalIncorrect =
      (agent.incorrectOrders || 0) +
      (agent.incorrectLines || 0) +
      (agent.incorrectWeight || 0) +
      (agent.incorrectVolume || 0) +
      (agent.incorrectQuantity || 0);
    const total = totalCorrect + totalIncorrect;
    if (total === 0) return 100;
    return Math.round((totalCorrect / total) * 100);
  };

  const getPerformanceStatus = (score) => {
    if (score >= 95)
      return { status: "Excellent", color: "#27ae60", className: "excellent" };
    if (score >= 85)
      return { status: "Good", color: "#3498db", className: "good" };
    if (score >= 75)
      return { status: "Average", color: "#f39c12", className: "average" };
    return { status: "Poor", color: "#e74c3c", className: "poor" };
  };

  useEffect(() => {
    if (token) {
      fetchData().then(() => setIsLoading(false));
      const interval = setInterval(() => {
        fetchData();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [token, fetchData]);

  useEffect(() => {
    const combined = [...kpiData, ...controllersKpiData, ...driversKpiData];
    setAllKpiData(combined);
  }, [kpiData, controllersKpiData, driversKpiData]);

  useEffect(() => {
    if (allKpiData.length > 0) {
      setDisplayedUsers(allKpiData.slice(0, 3));
      const rotationInterval = setInterval(() => {
        setDisplayedUsers((prev) => {
          const currentIndex = allKpiData.findIndex(
            (user) => user.userId === prev[0]?.userId,
          );
          const nextIndex = (currentIndex + 3) % allKpiData.length;
          if (nextIndex + 3 <= allKpiData.length) {
            return allKpiData.slice(nextIndex, nextIndex + 3);
          } else {
            const remaining = allKpiData.slice(nextIndex);
            return [...remaining, ...allKpiData.slice(0, 3 - remaining.length)];
          }
        });
      }, 20000);
      return () => clearInterval(rotationInterval);
    }
  }, [allKpiData]);

  if (!token) {
    return <Navigate to="/" replace />;
  }

  const calculateNetAmount = (correctTotal, incorrectTotal) => {
    return correctTotal - Math.abs(incorrectTotal);
  };

  const renderRankIcon = (index) => {
    if (index === 0) {
      return <FaTrophy className="rank-icon rank-icon-gold" />;
    } else if (index === 1) {
      return <FaMedal className="rank-icon rank-icon-silver" />;
    } else {
      return <FaMedal className="rank-icon rank-icon-bronze" />;
    }
  };

  const renderUserIcon = (userType) => {
    if (userType === "driver") {
      return <FaTruck className="user-type-icon" />;
    } else if (userType === "controller") {
      return <FaUserEdit className="user-type-icon" />;
    } else {
      return <GrUserWorker className="user-type-icon" />;
    }
  };

  return (
    <div className="dashboard-container">
      <div className="floating-bg floating-bg-1"></div>
      <div className="floating-bg floating-bg-2"></div>
      <div className="floating-bg floating-bg-3"></div>

      {/* Header Section */}
      <div className="page-header">
        <div className="header-top">
          <div className="header-left">
            <h2 className="dashboard-title">Warehouse Performance Dashboard</h2>
            <p className="dashboard-subtitle">Real-time performance metrics</p>
          </div>
          <div className="header-actions">
            <button
              className={`refresh-btn ${isRefreshing ? "spinning" : ""}`}
              onClick={async () => {
                setIsRefreshing(true);
                await fetchData();
                setIsRefreshing(false);
              }}
              disabled={isRefreshing}
              aria-label="Refresh dashboard data"
              title="Refresh dashboard data"
            >
              <FaSync /> {isRefreshing ? "Refreshing..." : "Refresh"}
            </button>
            <button
              className="logout-btn"
              onClick={handleLogout}
              aria-label="Logout"
              title="Logout from dashboard"
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </div>
      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      {/* KPI Summary Cards */}
      <div className="kpi-summary">
        <div className="kpi-card">
          <div className="kpi-icon">
            <FaUser />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Total Users</p>
            <p className="kpi-value">{allKpiData.length}</p>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon">
            <FaBox />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Total Orders</p>
            <p className="kpi-value">{totalOrders}</p>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon">
            <FaCheckCircle />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Avg Accuracy</p>
            <p className="kpi-value">
              {allKpiData.length > 0
                ? Math.round(
                    allKpiData.reduce(
                      (sum, user) => sum + calculatePerformanceScore(user),
                      0,
                    ) / allKpiData.length,
                  ) || 0
                : 0}
              %
            </p>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon">
            <FaStar />
          </div>
          <div className="kpi-content">
            <p className="kpi-label">Top Score</p>
            <p className="kpi-value">
              {allKpiData.length > 0
                ? Math.max(
                    ...allKpiData.map((user) =>
                      calculatePerformanceScore(user),
                    ),
                  ) || 0
                : 0}
              %
            </p>
          </div>
        </div>
      </div>

      {/* Top Agents Section */}
      <div className="top-agents-section">
        <h3 className="top-agents-title">Top Performing Agents</h3>
        <div className="top-agents-grid">
          {["Daily", "Monthly"].map((period) => (
            <div key={period} className="top-agents-card">
              <h4 className="top-agents-period">{period} Leaders</h4>
              <div className="leaders-container">
                {topAgents[period.toLowerCase()].length === 0 ? (
                  <div className="empty-state">No data available</div>
                ) : (
                  topAgents[period.toLowerCase()].map((agent, index) => (
                    <div
                      key={`${period}-${agent.userId}-${index}`}
                      className="leader-item"
                    >
                      <div className="leader-rank">{renderRankIcon(index)}</div>
                      <div className="leader-info">
                        <div className="leader-name">
                          {agent.userId === "N/A" ? "None" : agent.userId}
                        </div>
                        <div className="leader-count">
                          {agent.headerCount} orders
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* User Performance Grid */}
      <div className="targets-grid">
        {displayedUsers.length === 0 && !isLoading && (
          <div className="empty-state">
            <p>No KPI data available</p>
          </div>
        )}
        {displayedUsers.map((user) => {
          const score = calculatePerformanceScore(user);
          const status = getPerformanceStatus(score);

          return (
            <div
              key={user.userId}
              className={`target-card performance-${status.className}`}
            >
              <div className="target-card-header">
                <div className="agent-avatar">
                  {renderUserIcon(user.userType)}
                </div>
                <div className="agent-basic-info">
                  <h3 className="agent-name">{user.userId}</h3>
                  <div
                    className={`metric-amount ${
                      user.bonus >= 0 ? "positive" : "negative"
                    }`}
                  >
                    €{user.bonus.toFixed(2)}
                  </div>
                </div>

                {/* Performance Score */}
                <div className="performance-score-section">
                  <div className="score-display">
                    <div className="score-circle">
                      <svg viewBox="0 0 100 100" className="score-svg">
                        <circle cx="50" cy="50" r="45" className="score-bg" />
                        <circle
                          cx="50"
                          cy="50"
                          r="45"
                          className="score-fill"
                          style={{
                            strokeDasharray: `${(score / 100) * 283} 283`,
                            stroke: status.color,
                          }}
                        />
                      </svg>
                      <div className="score-text">{score}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Metrics */}
              <div className="target-card-body">
                <div className="metrics-grid">
                  <div className="metric-item">
                    <div className="metric-label">Orders</div>
                    <div className="metric-bar">
                      <div
                        className="metric-bar-fill"
                        style={{
                          width: `${Math.min(
                            (user.correctOrders /
                              Math.max(
                                user.correctOrders + user.incorrectOrders,
                                1,
                              )) *
                              100,
                            100,
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <div className="metric-stats">
                      <span className="metric-correct">
                        {user.correctOrders}
                      </span>
                      <span className="metric-sep">/</span>
                      <span className="metric-incorrect">
                        {user.incorrectOrders}
                      </span>
                    </div>
                  </div>

                  <div className="metric-item">
                    <div className="metric-label">Lines</div>
                    <div className="metric-bar">
                      <div
                        className="metric-bar-fill"
                        style={{
                          width: `${Math.min(
                            (user.correctLines /
                              Math.max(
                                user.correctLines + user.incorrectLines,
                                1,
                              )) *
                              100,
                            100,
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <div className="metric-stats">
                      <span className="metric-correct">
                        {user.correctLines}
                      </span>
                      <span className="metric-sep">/</span>
                      <span className="metric-incorrect">
                        {user.incorrectLines}
                      </span>
                    </div>
                  </div>

                  <div className="metric-item">
                    <div className="metric-label">Volume</div>
                    <div className="metric-bar">
                      <div
                        className="metric-bar-fill"
                        style={{
                          width: `${Math.min(
                            (user.correctVolume /
                              Math.max(
                                user.correctVolume + user.incorrectVolume,
                                1,
                              )) *
                              100,
                            100,
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <div className="metric-stats">
                      <span className="metric-correct">
                        {user.correctVolume}
                      </span>
                      <span className="metric-sep">/</span>
                      <span className="metric-incorrect">
                        {user.incorrectVolume}
                      </span>
                    </div>
                  </div>

                  <div className="metric-item">
                    <div className="metric-label">Quantity</div>
                    <div className="metric-bar">
                      <div
                        className="metric-bar-fill"
                        style={{
                          width: `${Math.min(
                            (user.correctQuantity /
                              Math.max(
                                user.correctQuantity + user.incorrectQuantity,
                                1,
                              )) *
                              100,
                            100,
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <div className="metric-stats">
                      <span className="metric-correct">
                        {user.correctQuantity}
                      </span>
                      <span className="metric-sep">/</span>
                      <span className="metric-incorrect">
                        {user.incorrectQuantity}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;
